import React, { useState } from 'react'

import { FormattedMessage, useIntl } from 'react-intl'
import Flex from 'styled-flex-component'

import { Button, CloseIcon, Modal, ModalTitle, TextArea } from '@tribegroup'
import { CommentTextAreaWrapper, WarningText } from './VibeCheckSelectionMode.styled'

interface IProps {
  // How many creators the confirm would reject. Derived by the parent from
  // the CURRENT selection on every render — never captured at open time.
  // Meaningless under `allPending` (the loaded page is not the set).
  count: number
  isOpen: boolean
  isRejecting: boolean
  onCancel: VoidFunction
  // Receives the trimmed comment only; the parent re-derives the selected
  // ids at confirm time (TRIBE FE stale-closure family guard — capturing the
  // ids when the modal opened shipped a bug in Admin once).
  onConfirm: (comment: string) => void
  // BRAND-9550: the parent escalated to a select-all-pending SCOPE FLAG. The
  // BE resolves the set, so this confirm covers creators that were never
  // loaded — and therefore never viewed.
  allPending?: boolean
  runScopeLabel?: 'all' | 'run'
}

// BRAND-9514: confirm step for bulk-rejecting vibe-check creators — count
// summary, optional shared comment, confirm/cancel. Analogue of the Inbox
// SelectionMode BulkDecline modal.
const BulkRejectModal: React.FC<IProps> = ({
  count,
  isOpen,
  isRejecting,
  onCancel,
  onConfirm,
  allPending = false,
  runScopeLabel = 'run',
}) => {
  const intl = useIntl()
  const [comment, setComment] = useState('')

  const handleConfirm = (): void => {
    onConfirm(comment.trim())
  }

  // Warren review (PR #3487): this modal stays mounted for the whole selection
  // session — only isOpen toggles — so a comment typed for one selection then
  // cancelled would otherwise persist and silently ride along with a LATER
  // selection's rejection (the BE persists it). Same stale-state family as the
  // confirm-time ids re-derivation (see onConfirm doc above). Clear it on
  // every user-initiated close (cancel button, X, ESC/backdrop), matching the
  // Inbox BulkDecline's cancel-time reset. Deliberately NOT cleared when the
  // parent hides the modal on a 422 failure (that path bypasses onCancel) —
  // the comment is kept for the retry, per the 422 selection-retention spec.
  const handleCancel = (): void => {
    setComment('')
    onCancel()
  }

  return (
    <Modal reactModalProps={{ isOpen, onRequestClose: handleCancel }} width={30} complex>
      <ModalTitle center>
        {allPending ? (
          <FormattedMessage id="creatorSearch.vibeCheck.bulkReject.modal.title.allPending" />
        ) : (
          <FormattedMessage
            id="creatorSearch.vibeCheck.bulkReject.modal.title"
            values={{ count }}
          />
        )}
      </ModalTitle>
      <CloseIcon onClick={handleCancel} />
      {/* AC 2 (BRAND-9550). The escalated set reaches creators the AM has
          never laid eyes on, and undo is per-creator only — it dies once
          that creator's intent link is opened, so there is no bulk way back.
          Say both, plainly, at the last point the user can still stop. Not
          shown for a plain page-select: there the user ticked every row
          themselves and the BRAND-9514 confirm is unchanged. */}
      {allPending && (
        <>
          <WarningText data-testid="bulk-reject-warning-scope">
            <FormattedMessage
              id="creatorSearch.vibeCheck.bulkReject.modal.allPending.warning.scope"
              values={{ scope: runScopeLabel }}
            />
          </WarningText>
          <WarningText data-testid="bulk-reject-warning-undo">
            <FormattedMessage id="creatorSearch.vibeCheck.bulkReject.modal.allPending.warning.undo" />
          </WarningText>
        </>
      )}
      {/* BRAND-9582 (QA item 18): wrapper adds the margin the shared
          TextArea's own character counter (CharacterCounter, internal to
          @tribegroup/TextArea) doesn't carry — see CommentTextAreaWrapper's
          doc for why the fix lives here and not in the shared component. */}
      <CommentTextAreaWrapper>
        <TextArea
          height={6}
          placeholder={intl.formatMessage({
            id: 'creatorSearch.vibeCheck.bulkReject.modal.comment.placeholder',
          })}
          value={comment}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
            setComment(event.currentTarget.value)
          }
          data-testid="bulk-reject-comment"
        />
      </CommentTextAreaWrapper>
      <Flex justifyCenter>
        <Button
          scheme="primary"
          outlined
          onClick={handleCancel}
          fontSize={0.875}
          height={3}
          width={10.125}
          data-testid="bulk-reject-cancel"
        >
          <FormattedMessage id="core.text.cancel" />
        </Button>
        <Button
          scheme="primary"
          fontSize={0.875}
          height={3}
          width={10.125}
          leftOuterSpacing={1}
          onClick={handleConfirm}
          // count === 0: every selected creator got decided elsewhere while
          // the modal sat open — "Reject 0 creators" must not be clickable
          // (Warren review); Cancel is the way out. Exempt under
          // `allPending` (BRAND-9550): the loaded-page count is not the set,
          // so a page that empties out says nothing about the unloaded
          // pendings still waiting to be rejected.
          disabled={isRejecting || (!allPending && count === 0)}
          loading={isRejecting}
          data-testid="bulk-reject-confirm"
        >
          <FormattedMessage id="creatorSearch.vibeCheck.bulkReject.modal.confirm" />
        </Button>
      </Flex>
    </Modal>
  )
}

export { BulkRejectModal }
export default BulkRejectModal
